import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import AdminPage from '../../components/admin/AdminPage';
import { deleteGenericSurvey, listGenericSurveys, getGenericSurveyAnalytics } from '../../services/adminService';

function Surveys() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  const [activeTab, setActiveTab] = useState('all'); // all, published, draft
  const [selectedType, setSelectedType] = useState('all'); // all, public, internal
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);

  const fetchList = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const nextPage = Math.max(Number(opts.page ?? page) || 1, 1);
      const nextSearch = String(opts.search ?? search ?? '');
      const resp = await listGenericSurveys({ page: nextPage, limit, search: nextSearch });
      if (!resp?.success) {
        toast.error(resp?.message || 'Failed to load surveys');
        setRows([]);
        setTotalRecords(0);
        return;
      }
      const surveys = resp.data?.surveys || [];
      
      // Fetch analytics for each survey to get real total_responses
      const analyticsPromises = surveys.map(s => getGenericSurveyAnalytics(s.id, { limit: 1 }));
      const analyticsResps = await Promise.all(analyticsPromises);
      
      const updatedSurveys = surveys.map((s, index) => {
        const analytics = analyticsResps[index]?.data;
        return {
          ...s,
          total_responses: analytics?.total_responses || 0,
          response_percentage: analytics?.response_percentage || 0
        };
      });

      setRows(updatedSurveys);
      setTotalRecords(Number(resp.data?.pagination?.total_records || 0));
      setPage(nextPage);
    } catch (e) {
      toast.error('Failed to load surveys');
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleCreate = () => {
    navigate('/admin/surveys/new');
  };

  const filteredSurveys = useMemo(() => {
    return rows.filter((s) => {
      const q = search.trim().toLowerCase();
      const status = String(s.status || '').toLowerCase();
      const isPublic = Boolean(s.is_public);
      
      const matchSearch = !q || String(s.title || '').toLowerCase().includes(q);
      const matchTab = activeTab === 'all' || 
                       (activeTab === 'published' && status === 'published') || 
                       (activeTab === 'draft' && status !== 'published' && status !== 'closed');
      const matchType = selectedType === 'all' || 
                        (selectedType === 'public' && isPublic) || 
                        (selectedType === 'internal' && !isPublic);
      
      return matchSearch && matchTab && matchType;
    });
  }, [rows, search, activeTab, selectedType]);

  const toggleActionMenu = (id) => {
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  useEffect(() => {
    const closeMenu = () => setActiveMenuId(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const totalPages = Math.max(Math.ceil(totalRecords / limit), 1);

  return (
    <AdminPage noPadding>
      <div className="w-full max-w-[1440px] mx-auto px-8 pt-6 pb-12 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-8 gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Manage Surveys</h1>
            <p className="text-gray-500 font-medium">
              Create, publish, assign and analyze surveys. Public surveys generate a shareable link.
            </p>
          </div>

          <button
            type="button"
            onClick={handleCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-white border border-blue-600 text-blue-600 px-6 py-2.5 text-sm font-semibold hover:bg-blue-50 transition-colors disabled:opacity-60 shadow-sm"
          >
            <Icon icon="fluent:add-24-regular" width="20" height="20" />
            Create Survey
          </button>
        </div>


        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center bg-gray-100/50 p-1 rounded-xl w-fit">
            {[
              { id: 'all', label: 'All' },
              { id: 'published', label: 'Published' },
              { id: 'draft', label: 'Drafts' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2 text-sm font-semibold rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#1e293b] text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTypeDropdown(!showTypeDropdown);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors min-w-[120px]"
              >
                <span className="text-gray-400">Type:</span>
                <span className="capitalize">{selectedType}</span>
                <Icon icon="fluent:chevron-down-24-regular" width="16" className={`ml-auto transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showTypeDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-100 rounded-xl shadow-xl z-20 py-1 overflow-hidden animate-in fade-in zoom-in duration-200">
                  {['all', 'public', 'internal'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setSelectedType(t);
                        setShowTypeDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm capitalize hover:bg-gray-50 transition-colors ${
                        selectedType === t ? 'text-blue-600 font-semibold bg-blue-50/50' : 'text-gray-700'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative w-full sm:w-64">
              <Icon icon="fluent:search-24-regular" width="18" className="text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
          </div>
        </div>


        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-[400px]">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-50 bg-gray-50/30">
                  <th className="px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Survey Name</th>
                  <th className="px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider text-center">Last Updated</th>
                  <th className="px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider text-center">Responses</th>
                  <th className="px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="py-20 text-center text-gray-500">
                      Loading surveys...
                    </td>
                  </tr>
                ) : filteredSurveys.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-20 text-center text-gray-500">
                      No surveys found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredSurveys.map((s, index) => {
                    const status = String(s.status || 'draft').toLowerCase();
                    const isPublished = status === 'published';
                    const lastUpdated = s.updated_at ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(s.updated_at)) : 'Oct 24, 2026';
                    
                    const responseCount = s.total_responses ?? 0;
                    const responsePct = s.response_percentage ?? 0;
                    const isNearBottom = filteredSurveys.length > 5 && index >= filteredSurveys.length - 2;

                    return (
                      <tr key={s.id} className="group hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                              {s.title || 'Untitled Survey'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${isPublished ? 'bg-emerald-50 border-emerald-100' : 'bg-gray-50 border-gray-100'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            <span className={`text-[11px] font-bold ${isPublished ? 'text-emerald-700' : 'text-gray-600'}`}>
                              {isPublished ? 'Published' : 'Draft'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="text-[13px] font-medium text-gray-700">{s.is_public ? 'Public' : 'Internal'}</span>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span className="text-[13px] font-medium text-gray-700">{lastUpdated}</span>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span className="text-[13px] font-bold text-gray-900">{responseCount}</span>
                        </td>
                        <td className="px-6 py-5 text-right relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleActionMenu(s.id);
                            }}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                          >
                            <Icon icon="fluent:more-horizontal-24-regular" width="20" height="20" />
                          </button>

                          {activeMenuId === s.id && (
                            <div className={`absolute right-6 ${isNearBottom ? 'bottom-full mb-2' : 'top-full mt-1'} w-40 bg-white border border-gray-100 rounded-xl shadow-xl z-[100] py-1 animate-in fade-in slide-in-from-top-2 duration-200`}>
                              <button
                                onClick={() => navigate(`/admin/surveys/${s.id}`)}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                              >
                                <Icon icon="fluent:edit-24-regular" width={16} />
                                Edit
                              </button>
                              <button
                                onClick={() => navigate(`/admin/surveys/${s.id}/analytics`)}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
                              >
                                <Icon icon="fluent:chart-multiple-24-regular" width={16} />
                                Analytics
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteTarget({ id: s.id, title: s.title || 'Untitled Survey' });
                                  setActiveMenuId(null);
                                }}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-red-600 hover:bg-red-50"
                              >
                                <Icon icon="fluent:delete-24-regular" width={16} />
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>


        {totalRecords > 0 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-6">
            <div className="text-[13px] font-medium text-gray-400 tracking-tight">
              Showing <span className="text-gray-900 font-bold">{filteredSurveys.length}</span> out of <span className="text-gray-900 font-bold">{totalRecords}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                {/* First Page */}
                <button
                  onClick={() => fetchList({ page: 1 })}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-xs font-bold border transition-all ${
                    page === 1 ? 'bg-[#101828] border-[#101828] text-white' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  1
                </button>

                {page > 2 && <span className="px-1.5 text-gray-400 text-xs font-bold leading-none">...</span>}

                {/* Prev Icon */}
                <button
                  disabled={page <= 1}
                  onClick={() => fetchList({ page: page - 1 })}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  <Icon icon="fluent:chevron-left-24-regular" width="16" />
                </button>
                
                {/* Current Page */}
                {page !== 1 && page !== totalPages && (
                  <button
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-xs font-bold bg-[#101828] border border-[#101828] text-white shadow-sm"
                  >
                    {page}
                  </button>
                )}

                {/* Next Icon */}
                <button
                  disabled={page >= totalPages}
                  onClick={() => fetchList({ page: page + 1 })}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                >
                  <Icon icon="fluent:chevron-right-24-regular" width="16" />
                </button>

                {page < totalPages - 1 && <span className="px-1.5 text-gray-400 text-xs font-bold leading-none">...</span>}

                {/* Last Page */}
                {totalPages > 1 && (
                  <button
                    onClick={() => fetchList({ page: totalPages })}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-xs font-bold border transition-all ${
                      page === totalPages ? 'bg-[#101828] border-[#101828] text-white' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {totalPages}
                  </button>
                )}
              </div>

              <div className="relative ml-2">
                <input
                  type="text"
                  placeholder="Page"
                  className="w-[100px] h-9 rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-900 transition-all shadow-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val) && val >= 1 && val <= totalPages) {
                        fetchList({ page: val });
                      }
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {deleteTarget ? (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900">Delete Survey</h3>
                  <button 
                    onClick={() => setDeleteTarget(null)}
                    className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
                  >
                    <Icon icon="fluent:dismiss-24-regular" width={20} />
                  </button>
                </div>
                <p className="text-sm text-gray-500">This action cannot be undone. Are you sure you want to delete this survey?</p>
              </div>

              <div className="p-6 bg-gray-50/50">
                <div className="bg-white p-4 rounded-xl border border-red-50 mb-6">
                  <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">survey title</span>
                  <p className="text-sm font-bold text-gray-900 mt-0.5">{deleteTarget.title}</p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setDeleteTarget(null)}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      setDeleting(true);
                      try {
                        const resp = await deleteGenericSurvey(deleteTarget.id);
                        if (!resp?.success) {
                          toast.error(resp?.message || 'Failed to delete');
                          return;
                        }
                        toast.success('Deleted');
                        setDeleteTarget(null);
                        await fetchList();
                      } catch (e) {
                        toast.error('Error deleting');
                      } finally {
                        setDeleting(false);
                      }
                    }}
                    disabled={deleting}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

      </div>
    </AdminPage>
  );
}

export default Surveys;
